import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import { CanAccess, useMenu, useTranslation } from "@refinedev/core";
import { TreeMenuItem } from "@refinedev/core/dist/hooks/menu/useMenu";
import { Dispatch, Fragment, ReactNode, SetStateAction, useEffect, useState } from "react";
import { Collapse } from "react-collapse";
import { Link, useLocation } from "react-router-dom";
import { UserType } from "interfaces";
import {
  canViewWpsMenuItem,
  canViewWpsSubMenuItem,
  wpsMenuData,
  WpsDashboardPermission,
  WpsMenuItem,
} from "config/wpsMenu";

type sidebarProps = {
  open: boolean;
  onClose: () => void;
  permissions?: WpsDashboardPermission[] | null;
  type?: UserType | string;
}

interface SubMenuProps {
  id: string;
  name?: string;
  label?: string;
  children: ReactNode;
  icon?: ReactNode;
  parentOpenKey: string;
  setParentOpenKey: Dispatch<SetStateAction<string>>;
  defaultOpen?: boolean;
}

const SubMenu = ({children, name, label, id, icon, parentOpenKey, setParentOpenKey, defaultOpen}: SubMenuProps) => {
  const { defaultOpenKeys } = useMenu();

  const [isOpened, setIsOpened] = useState(defaultOpen ?? defaultOpenKeys.includes(id))

  // useEffect(() => {
  //   if (defaultOpenKeys.includes(id)) {
  //     setParentOpenKey(id);
  //     // setParentOpenKeys([...parentOpenKeys, id]);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const _handleToggleMenu = () => {
    // setParentOpenKey(prevOpenKey => prevOpenKey === id ? "" : id);
    setIsOpened(!isOpened);
  }

  return (
    <li key={id} className="menu-item">
      <button 
        className="w-full flex justify-between items-center text-sm rounded-lg px-3 py-2 leading-6 font-semibold text-neutral-800 hover:bg-magnolia-500" 
        onClick={_handleToggleMenu}
      >
        <span className="flex">
          {icon} {name ?? label}
        </span>
        {isOpened 
          ? <ChevronDownIcon className="w-4 h-4 text-brand-500" /> 
          : <ChevronUpIcon className="w-4 h-4 text-brand-500" />}
      </button>
      <Collapse isOpened={isOpened}>
        <ul className="flex flex-col gap-y-2 px-3 py-2">{children}</ul>
      </Collapse>
    </li>
  )
}

export const SidebarModal = (props: sidebarProps) => {
  const location = useLocation();
  const { menuItems, selectedKey } = useMenu();
  const { translate } = useTranslation();

  const [parentOpenKey, setParentOpenKey] = useState<string>("");

  const { open, onClose, permissions, type = UserType.ADMIN } = props;
  const isWps = type === UserType.WPS;
  const logoSrc = isWps ? "/images/logo/logo-wps.png" : "/images/logo/colored.png";
  const logoClassName = isWps ? "h-[44px] w-auto object-cover" : "w-24";
  const logoAlt = isWps ? "Actiup WPS Logo" : "Actiup Logo";

  const getWpsMenuLabel = (item: WpsMenuItem) => {
    if (item.labelKey) {
      return translate(item.labelKey);
    }

    return item.label ?? item.id;
  };

  const isWpsItemSelected = (item: WpsMenuItem) => {
    if (item.id === "dashboard") {
      return location.pathname.startsWith("/dashboard");
    }

    return !!item.url && location.pathname.includes(item.url);
  };

  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const renderMenuItems = (items: TreeMenuItem[]) => {
    return items.map(({ key, name, label, icon, route, children, list, identifier }) => {
      if (children.length > 0) {
        return (
          <SubMenu 
            key={key} 
            id={key} 
            name={name} 
            label={label} 
            icon={icon}
            parentOpenKey={parentOpenKey}
            setParentOpenKey={setParentOpenKey}
          >
            {renderMenuItems(children)}
          </SubMenu>
        );
      }

      const isSelected = key === selectedKey;

      return (
        <CanAccess key={name} resource={identifier ?? name} action="list">
          <li key={key}>
            <Link  
              to={route ?? "/"}
              className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
            >
              {icon} {name}
            </Link>
          </li>
        </CanAccess>
      );
    })
  };

  const renderWpsMenuItems = (items: WpsMenuItem[]) => {
    return items.map((menuItem) => {
      if (!canViewWpsMenuItem(menuItem, permissions ?? [])) {
        return null;
      }

      if (menuItem.subMenu && menuItem.subMenu.length > 0) {
        const permittedSubMenuItems = menuItem.subMenu.filter((item) =>
          canViewWpsSubMenuItem(menuItem.id, item, permissions ?? [])
        );

        if (permittedSubMenuItems.length === 0) {
          return null;
        }

        return (
          <SubMenu
            key={menuItem.id}
            id={menuItem.id}
            name={getWpsMenuLabel(menuItem)}
            icon={menuItem.icon as ReactNode}
            parentOpenKey={parentOpenKey}
            setParentOpenKey={setParentOpenKey}
            defaultOpen={permittedSubMenuItems.some(isWpsItemSelected)}
          >
            {permittedSubMenuItems.map((item) => {
              const isSelected = isWpsItemSelected(item);

              return (
                <li key={`${menuItem.id}-${item.id}`}>
                  <Link
                    to={item.url}
                    className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
                  >
                    {item.icon} {getWpsMenuLabel(item)}
                  </Link>
                </li>
              );
            })}
          </SubMenu>
        );
      }

      const isSelected = isWpsItemSelected(menuItem);

      return (
        <li key={menuItem.id}>
          <Link
            to={menuItem.url}
            className={`flex rounded-lg px-3 py-2 text-sm leading-6 font-semibold text-neutral-800 ${isSelected ? "bg-magnolia-500" : "hover:bg-magnolia-500"}`}
          >
            {menuItem.icon} {getWpsMenuLabel(menuItem)}
          </Link>
        </li>
      );
    });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      {/* BACKDROP */}
      <Dialog 
        as="div" 
        className="relative z-50 lg:hidden"
        onClose={onClose}
      > 
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex w-full max-w-xs flex-1">
              {/* Sidebar */}
              <div className="w-full overflow-y-auto bg-white pt-2 px-4 pb-4 ring-1 ring-white/10">
                <div className="flex h-16 shrink-0 items-center justify-between">
                  <img
                    src={logoSrc}
                    alt={logoAlt}
                    className={logoClassName}
                  />
                  <div className="self-start pt-3">
                    <button 
                      type="button" 
                      onClick={onClose}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon className="h-6 w-6 text-gray-900" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <ul className="flex flex-1 flex-col gap-y-2 mt-4">
                  {isWps ? renderWpsMenuItems(wpsMenuData) : renderMenuItems(menuItems)}
                </ul>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>

  )
}  
